<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, email, minLength } from "@vuelidate/validators";
import type { Schemas } from "#shopware";
const { appliedPromotionCodes, addPromotionCode, removeItem } = useCart();
const { getErrorsCodes } = useCartNotification();
const { pushError } = useNotifications();

const { t } = useI18n();

const isProcessing = ref(false);
const addPromotionCodeHandler = async (code: string, validation: any) => {
  validation.$touch();
  if (validation.$error) return;
  isProcessing.value = true;
  await addPromotionCode(code);
  getErrorsCodes()?.forEach((element) => {
    pushError(t(`errors.${element.messageKey}`, { ...element }));
  });
  state.promoCode = "";
  isProcessing.value = false;
};

const removeItemHandler = (appliedPromotionCode: Schemas["LineItem"]) => {
  removeItem(appliedPromotionCode);
  getErrorsCodes()?.forEach((element) => {
    pushError(t(`errors.${element.messageKey}`, { ...element }));
  });
};

const showPromotionCodes = computed(() => appliedPromotionCodes.value.length > 0);

const rules = computed(() => ({
  promoCode: {
    required,
    minLength: minLength(3),
  },
}));

const state = reactive({
  promoCode: "",
});

const $v = useVuelidate(rules, state);
const promoCodeForm = ref(false);

const togglePromoCode = () => {
  promoCodeForm.value = !promoCodeForm.value;
};
</script>

<template>
  <!-- Promo Code Section -->
  <div class="">
    <div
      class="flex justify-between items-center cursor-pointer mb-8"
      @click="togglePromoCode"
    >
      <h6 class="text-chinese-black font-normal">{{ $t("checkout.promoCode.applyPromoCode") }}</h6>
      <span class="text-sm">
        <i
          id="toggleIcon"
          class="fa-solid fa-angle-down transition-transform duration-300"
          :class="{
            'rotate-180': promoCodeForm,
          }"
        ></i>
      </span>
    </div>

    <!-- Promo Code Form (Initially Hidden) -->
    <div
      v-if="promoCodeForm"
      class="mt-4 flex justify-between"
    >
      <div class="relative w-[calc(100%-138px)]">
        <input
          v-model="state.promoCode"
          type="text"
          maxlength="19"
          name="promoCode"
          :placeholder="$t('form.promoCodePlaceholder')"
          class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
          :class="[
            $v.promoCode?.$error
              ? 'border-red-600 focus:border-red-600 border-2'
              : 'border-gray-300 focus:border-gray-500',
          ]"
          @blur="$v.promoCode?.$touch()"
        />
        <label
          for="promoCode"
          class="pointer-events-none input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
          :class="{
            'transform -translate-y-5 text-xs px-2': state.promoCode || $v.promoCode.$error,
            'text-red-500': $v.promoCode.$error,
            'text-dark-bg': !$v.promoCode.$error,
          }"
        >
          Voucher
        </label>
        <p
          v-if="$v.promoCode.$error"
          class="text-red-500 text-xs mt-1"
        >
          {{ $v.promoCode.$errors[0].$message }}
        </p>
      </div>

      <div class="apply w-[118px]">
        <button
          class="bg-transparent text-base font-bold text-dark-bg py-3 px-9 border border-dark-bg rounded-full hover:bg-red-bg hover:text-white hover:border-red-bg transition duration-300"
          @click="addPromotionCodeHandler(state.promoCode, $v.promoCode)"
        >
          <span v-if="!isProcessing">{{ $t("checkout.promoCode.apply") }}</span>
          <i v-else class="fa-solid fa-spinner fa-spin"></i>
        </button>
      </div>
    </div>

    <div v-if="showPromotionCodes">
      <div>{{ $t("checkout.promoCode.label") }}:</div>
      <ul role="list" class="text-sm pl-0">
        <li
          v-for="appliedPromotionCode in appliedPromotionCodes"
          :key="appliedPromotionCode.id"
          class="flex justify-between text-secondary-600 border-b py-4"
        >
          <span>{{ appliedPromotionCode.label }}</span>
          <button
            class="text-black"
            type="button"
            @click="removeItemHandler(appliedPromotionCode)"
          >
            {{ $t("checkout.promoCode.remove") }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
